import { invokeV4, tableRows } from "../../shared/api/v4Remote";
import type {
  AccountRecord,
  CreateAccountInput,
  CreateAssignmentInput,
  CrossStoreAssignment,
  DisableAccountInput,
  OrganizationAudit,
  OrganizationState,
  SetMemberCoachesInput,
  StoreRecord,
  UpdateMemberInput,
  UpdateStaffInput,
} from "./organization.types";

type Row = Record<string, unknown>;
const camel = (row: Row) =>
  ({
    id: String(row.id),
    phone: String(row.phone ?? ""),
    name: String(row.name ?? ""),
    role: row.role,
    status: row.status,
    storeId: row.store_id ?? null,
    jobTitle: String(row.job_title ?? ""),
    assignedMemberCount: Number(row.assigned_member_count ?? 0),
    openTaskCount: Number(row.open_task_count ?? 0),
    handoverTo: row.handover_to ?? null,
    createdAt: String(row.created_at ?? ""),
    gender: row.gender ?? null,
    birthday: row.birthday ? String(row.birthday) : null,
    coachId: row.coach_id ?? null,
    serviceCoachIds: Array.isArray(row.service_coach_ids)
      ? Array.from(new Set(row.service_coach_ids.map(String)))
      : [],
    memberStatus: row.member_status ?? null,
  }) as AccountRecord;

export const remoteOrganizationRepository = {
  async getState(): Promise<OrganizationState> {
    const [stores, profiles, employees, members, memberCoaches, assignments, audit] =
      await Promise.all([
        tableRows<Row>("stores", "id,code,name,status"),
        tableRows<Row>(
          "profiles",
          "id,phone,name,role,status,store_id,created_at",
        ),
        tableRows<Row>("employee_profiles", "profile_id,job_title,birthday"),
        tableRows<Row>(
          "member_profiles",
          "profile_id,gender,birthday,coach_id,status",
        ),
        tableRows<Row>(
          "member_coach_assignments",
          "member_id,coach_id,assignment_type",
        ),
        tableRows<Row>(
          "cross_store_assignments",
          "id,employee_id,store_id,member_id,starts_at,ends_at,approved_by,reason",
        ),
        tableRows<Row>(
          "audit_logs",
          "id,actor_id,action,entity_type,entity_id,reason,occurred_at",
        ),
      ]);
    const employeeDetails = new Map(employees.map((row) => [row.profile_id, row]));
    const memberDetails = new Map(members.map((row) => [row.profile_id, row]));
    const now = Date.now();
    const serviceCoachIds = new Map<string, Set<string>>();
    const addServiceCoach = (memberId: unknown, coachId: unknown) => {
      if (!memberId || !coachId) return;
      const key = String(memberId);
      const coaches = serviceCoachIds.get(key) ?? new Set<string>();
      coaches.add(String(coachId));
      serviceCoachIds.set(key, coaches);
    };
    memberCoaches.forEach((row) => addServiceCoach(row.member_id, row.coach_id));
    assignments.forEach((row) => {
      if (
        row.member_id &&
        now >= new Date(String(row.starts_at)).getTime() &&
        now < new Date(String(row.ends_at)).getTime()
      ) addServiceCoach(row.member_id, row.employee_id);
    });
    return {
      stores: stores as unknown as StoreRecord[],
      accounts: profiles.map((row) => {
        const detail = memberDetails.get(row.id);
        const employeeDetail = employeeDetails.get(row.id);
        return camel({
          ...row,
          job_title: employeeDetail?.job_title ?? "",
          gender: detail?.gender,
          birthday: detail?.birthday ?? employeeDetail?.birthday,
          coach_id: detail?.coach_id,
          service_coach_ids: [
            ...(serviceCoachIds.get(String(row.id)) ?? new Set<string>()),
            ...(detail?.coach_id ? [String(detail.coach_id)] : []),
          ],
          member_status: detail?.status,
        });
      }),
      assignments: assignments.map(
        (row) =>
          ({
            id: String(row.id),
            employeeId: String(row.employee_id),
            storeId: String(row.store_id),
            memberId: row.member_id ? String(row.member_id) : null,
            startsAt: String(row.starts_at),
            endsAt: String(row.ends_at),
            approvedBy: String(row.approved_by),
            reason: String(row.reason),
            status:
              now < new Date(String(row.starts_at)).getTime()
                ? "scheduled"
                : now > new Date(String(row.ends_at)).getTime()
                  ? "expired"
                  : "active",
          }) as CrossStoreAssignment,
      ),
      audit: audit.map(
        (row) =>
          ({
            id: String(row.id),
            actorId: String(row.actor_id ?? ""),
            action: String(row.action),
            entityType: row.entity_type,
            entityId: String(row.entity_id),
            reason: String(row.reason ?? ""),
            occurredAt: String(row.occurred_at),
          }) as OrganizationAudit,
      ),
    };
  },
  createAccount(_actor: unknown, input: CreateAccountInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "createAccount", {
      ...input,
    });
  },
  updateStaff(_actor: unknown, input: UpdateStaffInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "updateStaffAccount", {
      ...input,
    });
  },
  updateMember(_actor: unknown, input: UpdateMemberInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "updateMemberProfile", {
      ...input,
    });
  },
  setMemberCoaches(_actor: unknown, input: SetMemberCoachesInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "setMemberCoaches", {
      ...input,
    });
  },
  disableAccount(_actor: unknown, input: DisableAccountInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "setAccountStatus", {
      accountId: input.accountId,
      status: "disabled",
      reason: input.reason,
      handoverTo: input.handoverTo ?? null,
    });
  },
  restoreAccount(_actor: unknown, accountId: string, reason: string) {
    return invokeV4<{ id: string }>("v4-account-admin", "setAccountStatus", {
      accountId,
      status: "active",
      reason,
    });
  },
  createAssignment(_actor: unknown, input: CreateAssignmentInput) {
    return invokeV4<{ id: string }>("v4-account-admin", "createAssignment", {
      ...input,
    });
  },
};
